import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client.js';

/**
 * Pick a medical scheme; be told what it means.
 *
 * THE POINT IS NOT THE DROPDOWN. Choosing a scheme fills in the administrator —
 * the spreadsheet's XLOOKUP — but the thing that actually saves money is the
 * sentence underneath it: whether THIS scheme needs authorisation for THIS kind
 * of job, right now, while the caller is still on the phone.
 *
 * Today that knowledge lives in whoever is on shift. When they are wrong, the
 * claim is rejected six weeks later, the patient is home, and nobody can
 * reconstruct the call.
 *
 * The answer is computed server-side and arrives as one instruction, because
 * the rule combines four columns with the call type and there should be exactly
 * one place that knows how — the same place the billing guard will read.
 */

interface AuthAdvice {
  preAuth: 'yes' | 'no' | 'private' | null;
  postAuth: 'yes' | 'no' | 'private' | null;
  summary: string;
  note: string | null;
}

interface Scheme {
  id: string;
  name: string;
  administrator: string | null;
  auth: AuthAdvice;
}

/** Red only for "stop and get a number". Everything else must not cry wolf. */
function toneFor(auth: AuthAdvice): string {
  if (auth.preAuth === 'yes') return 'border-red-500/40 bg-red-500/10 text-red-200';
  if (auth.postAuth === 'yes') return 'border-amber-500/40 bg-amber-500/10 text-amber-200';
  if (auth.preAuth === 'private' || auth.postAuth === 'private') {
    return 'border-sky-500/40 bg-sky-500/10 text-sky-200';
  }
  if (auth.preAuth === null) return 'border-line bg-surface-3 text-slate-400';
  return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200';
}

export function SchemePicker({
  value,
  administrator,
  responseKind,
  onSelect,
}: {
  value: string;
  administrator: string | null;
  /** Decides which of the four authorisation columns applies. */
  responseKind?: 'primary' | 'transfer';
  onSelect: (scheme: { name: string; administrator: string | null }) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Held so the advice stays on screen after the list closes — the dispatcher
  // needs to keep reading it, not lose it the moment they click.
  const [chosen, setChosen] = useState<Scheme | null>(null);

  const { data } = useQuery({
    queryKey: ['schemes', query, responseKind],
    queryFn: () =>
      api.get<{ schemes: Scheme[] }>(
        `/medical-schemes/search?q=${encodeURIComponent(query)}` +
          (responseKind ? `&responseKind=${responseKind}` : ''),
      ),
    enabled: open && query.trim().length >= 2,
  });

  // Re-fetched when the call type changes so the advice follows it. A card that
  // said "no authorisation needed" for a primary and kept saying it after the
  // job became a transfer would be worse than showing nothing.
  const { data: current } = useQuery({
    queryKey: ['scheme-advice', value, responseKind],
    queryFn: () =>
      api.get<{ schemes: Scheme[] }>(
        `/medical-schemes/search?q=${encodeURIComponent(value)}` +
          (responseKind ? `&responseKind=${responseKind}` : ''),
      ),
    enabled: Boolean(value) && !open,
  });

  const advice =
    chosen?.name === value
      ? chosen.auth
      : (current?.schemes.find((s) => s.name === value)?.auth ?? null);

  return (
    <div className="space-y-2">
      <div className="relative">
        <label className="block text-xs text-slate-400">Medical aid / scheme</label>
        <input
          value={open ? query : value}
          placeholder="Start typing — Discovery, Bonitas…"
          autoComplete="off"
          onFocus={() => {
            setOpen(true);
            setQuery(value);
          }}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="mt-1 w-full rounded border border-line bg-surface-2 px-2 py-1.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-sky-600 focus:outline-none"
        />

        {open && data?.schemes.length ? (
          <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-line bg-surface-3 shadow-xl">
            {data.schemes.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setChosen(s);
                    setOpen(false);
                    onSelect({ name: s.name, administrator: s.administrator });
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-sky-600/20 hover:text-slate-100"
                >
                  <span className="block">{s.name}</span>
                  <span className="block text-xs text-slate-500">
                    {s.administrator ?? 'self-administered'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {administrator && (
        <p className="text-xs text-slate-400">
          Administrator: <span className="text-slate-200">{administrator}</span>
          <span className="ml-1.5 text-slate-600">· filled in from the scheme</span>
        </p>
      )}

      {advice && (
        <div className={`rounded-lg border px-3 py-2 text-xs ${toneFor(advice)}`}>
          <p className="font-medium">{advice.summary}</p>
          {advice.note && (
            // The eleven schemes whose rule is prose rather than yes/no. Shown
            // verbatim: paraphrasing an exception is how it stops being one.
            <p className="mt-1 text-[11px] opacity-90">{advice.note}</p>
          )}
        </div>
      )}
    </div>
  );
}
